<script setup lang="ts">
import { getHyphenatedCurrentLocale } from '@@/bits/current_locale'
import device from '@@/bits/device'
import { isEmail } from '@@/bits/email'
import { escapeHtml } from '@@/bits/html_parser'
import { __ } from '@@/bits/intl'
import { reload as reloadPage, transformCurrentUrl } from '@@/bits/location'
import OzBackButton from '@@/library/v4/components/OzBackButton.vue'
import OzContainedButton, { OzContainedButtonColorScheme } from '@@/library/v4/components/OzContainedButton.vue'
import OzInput from '@@/library/v4/components/OzInput.vue'
import OzLabelInput from '@@/library/v4/components/OzLabelInput.vue'
import OzPadletLogo from '@@/library/v4/components/OzPadletLogo.vue'
import {
  RequestResetPasswordStatus,
  RequestResetPasswordStep,
  useRequestResetPasswordStore,
} from '@@/pinia/request_reset_password_store'
import { useWindowSizeStore } from '@@/pinia/window_size'
import AuthBottomNav from '@@/vuecomponents/AuthBottomNav.vue'
import GlobalSnackbar from '@@/vuecomponents/GlobalSnackbar.vue'
import SkipToContentLink from '@@/vuecomponents/SkipToContentLink.vue'
import { storeToRefs } from 'pinia'
import { computed, ref, watch } from 'vue'

const RESEND_EMAIL_DELAY_IN_SECONDS = 15
const SECONDS_FORMATTER = new Intl.NumberFormat(getHyphenatedCurrentLocale(), {
  style: 'unit',
  unit: 'second',
  unitDisplay: 'narrow',
})

const windowSizeStore = useWindowSizeStore()
const { isSmallerThanTabletPortrait } = storeToRefs(windowSizeStore)

const requestResetPasswordStore = useRequestResetPasswordStore()
const { isEmailValid, email, emailValidationMessage, requestResetPasswordStatus, requestResetPasswordStep } =
  storeToRefs(requestResetPasswordStore)
const {
  requestResetPassword,
  sendRequestResetPasswordEmail,
  setEmail,
  setEmailValidationMessage,
  setRequestResetPasswordStatus,
} = requestResetPasswordStore

const emailInput = ref<InstanceType<typeof OzInput>>()
const isEmailInputFocused = ref(false)
const initialScrollY = ref(0)
const resendEmailCountdownIntervalId = ref<number | undefined>(undefined)
const resendEmailTimeoutId = ref<number | undefined>(undefined)
const secondsToResend = ref<number>(0)

const xHeader = computed(() => !(device.mobile && device.android && isSmallerThanTabletPortrait))

const formattedSecondsToResend = computed<string>(() => SECONDS_FORMATTER.format(secondsToResend.value))

const canSubmitRequestResetPasswordForm = computed<boolean>(
  () =>
    email.value.length !== 0 &&
    isEmailValid.value &&
    requestResetPasswordStatus.value !== RequestResetPasswordStatus.Updating,
)

const signUpWithEmailUrl = computed<string>(() =>
  transformCurrentUrl({}, { path: '/auth/signup', search: { email: email.value } }),
)

const knownEmailDescription = computed<string>(() => {
  if (requestResetPasswordStep.value === RequestResetPasswordStep.KnownEmail)
    return __(
      'We’ve sent a password recovery link to <span class="text-grape-500 dark:text-canary-500">%{email}</span>.',
      {
        email: escapeHtml(email.value),
      },
    )
  if (requestResetPasswordStep.value === RequestResetPasswordStep.KnownEmailResent)
    return __('We’ve resent the link. Please check your inbox for next steps.', {
      email: escapeHtml(email.value),
    })
  return ''
})

const contactUsUrl = computed<string>(() => {
  const params = new URLSearchParams({
    selected: 'Account / Login',
    message: `I am unable to reset my password for: ${email.value}`,
  })
  return `https://padlet.com/contact-us?${params.toString()}`
})

const knownEmailContactUsText = computed<string>(() =>
  __(
    "Didn't get the email? Check your spam folder or <a class='oz-text-link-light-secondary dark:oz-text-link-dark-secondary font-semibold tablet-portrait:font-normal' href='%{contactUsUrl}' target='_blank'>contact us</a>.",
    { contactUsUrl: escapeHtml(contactUsUrl.value) },
  ),
)

const unknownEmailDescription = computed<string>(() =>
  __(
    'Sorry, we couldn’t find that email on file. You can try another email or sign up with <span class="text-grape-500 dark:text-canary-500">%{email}</span>.',
    {
      email: escapeHtml(email.value),
    },
  ),
)

const unknownEmailContactUsText = computed<string>(() =>
  __(
    'Need help? <a class="oz-text-link-light-secondary dark:oz-text-link-dark-secondary font-semibold tablet-portrait:font-normal" href="%{contactUsUrl}" target="_blank">Contact us</a>',
    { contactUsUrl: escapeHtml(contactUsUrl.value) },
  ),
)

const logInWithEmailUrl = computed<string | undefined>(() => {
  if (isEmail(email.value)) {
    return transformCurrentUrl({}, { path: '/auth/login', search: { email: email.value } })
  }
  return transformCurrentUrl({}, { path: '/auth/login' })
})

const handleBackButton = (): void => {
  requestResetPasswordStep.value = RequestResetPasswordStep.Submission
  resetResendEmailCountdown()
}

const tryAnotherEmail = (): void => {
  setEmail('')
  requestResetPasswordStep.value = RequestResetPasswordStep.Submission
}

const resendRequestResetPasswordEmail = (): void => {
  requestResetPasswordStep.value = RequestResetPasswordStep.KnownEmailResent
  sendRequestResetPasswordEmail()
  startResendEmailCountdown()
}

/********************
 * FORM             *
 ********************/

const onInputEmailInput = (value: string): void => {
  setEmail(value)
  setRequestResetPasswordStatus(RequestResetPasswordStatus.Initial)

  // Reset the password validation message to enable the "Request password reset" button
  if (emailValidationMessage.value.length) setEmailValidationMessage('')
}

const isExceededScrollableThreshold = (currentWindowY: number): boolean => {
  const SCROLLABLE_THRESHOLD = 40 // the height of the input field
  return (
    currentWindowY > initialScrollY.value + SCROLLABLE_THRESHOLD ||
    currentWindowY < initialScrollY.value - SCROLLABLE_THRESHOLD
  )
}

const blurEmailInputOnScroll = (): void => {
  // Note: the scroll event is fired when people press a key on the soft keyboard only on iPad
  // we just blur the input only if the user scrolls past a certain threshold
  if (isExceededScrollableThreshold(window.scrollY)) {
    emailInput.value?.blurInput()
  }
}

const onFocusInEmailInput = (): void => {
  isEmailInputFocused.value = true
}

const onBlurEmailInput = (): void => {
  isEmailInputFocused.value = false
  document.removeEventListener('scroll', blurEmailInputOnScroll)
}

const submitRequestResetPasswordForm = (): void => {
  if (!canSubmitRequestResetPasswordForm.value) return
  requestResetPassword()
}

watch(
  () => windowSizeStore.visualViewportHeight,
  (newHeight, oldHeight) => {
    if (device.desktop || newHeight >= oldHeight) return

    initialScrollY.value = window.scrollY

    // When people focus on the input, the keyboard will pop up
    // so the visual viewport height will be narrowed down.
    // we want to hide the keyboard when people try to scroll the page
    if (isEmailInputFocused.value) {
      document.addEventListener('scroll', blurEmailInputOnScroll)
    }
  },
)

/********************
 * RESEND COUNTDOWN *
 ********************/

const stopResendEmailCountdownInterval = (): void => {
  clearInterval(resendEmailCountdownIntervalId.value)
  resendEmailCountdownIntervalId.value = undefined
}

const stopResendEmailCountdown = (): void => {
  clearTimeout(resendEmailTimeoutId.value)
  resendEmailTimeoutId.value = undefined
}

const startResendEmailCountdown = (): void => {
  secondsToResend.value = RESEND_EMAIL_DELAY_IN_SECONDS
  resendEmailCountdownIntervalId.value = setInterval(() => {
    secondsToResend.value -= 1
  }, 1000) as unknown as number
  resendEmailTimeoutId.value = setTimeout(() => {
    resetResendEmailCountdown()
  }, RESEND_EMAIL_DELAY_IN_SECONDS * 1000) as unknown as number
}

const resetResendEmailCountdown = (): void => {
  stopResendEmailCountdownInterval()
  stopResendEmailCountdown()
  secondsToResend.value = 0
}
</script>

<template>
  <div class="relative flex flex-col items-center">
    <header
      v-show="xHeader"
      :class="[
        'fixed flex items-center justify-between box-border w-full transition-all',
        device.desktop ? 'h-14 ps-5 pe-4' : 'h-18 px-4 tablet-portrait:ps-6',
      ]"
    >
      <SkipToContentLink />

      <OzPadletLogo v-if="device.desktop" :class="[{ invisible: device.electronApp }]" />
      <OzBackButton
        v-else-if="requestResetPasswordStep === RequestResetPasswordStep.Submission"
        :href="logInWithEmailUrl"
      />
      <OzBackButton v-else @click="handleBackButton" />
    </header>

    <main
      id="main-content"
      :class="[
        'fixed',
        'bottom-0',
        'flex',
        'flex-col',
        'box-border',
        'w-full',
        'p-4',
        'tablet-portrait:relative tablet-portrait:w-[375px] tablet-portrait:p-0 tablet-portrait:px-4 tablet-portrait:mt-32',
      ]"
      tabindex="-1"
    >
      <template v-if="requestResetPasswordStep === RequestResetPasswordStep.Submission">
        <div class="text-dark-text-100 dark:text-light-text-100 mb-5">
          <OzBackButton v-if="device.desktop" class="mb-5" :href="logInWithEmailUrl" />
          <h1 class="w-56 text-login-heading-1 font-semibold px-2 mb-1">
            {{ __('Reset password') }}
          </h1>
          <h2 class="text-login-body px-2">
            {{ __('Enter your email address to receive a password reset link.') }}
          </h2>
        </div>

        <form
          novalidate
          :class="['flex flex-col mb-6', { 'tablet-portrait:mb-[72px]': !windowSizeStore.isSmallerThanTabletPortrait }]"
          @submit.stop.prevent="submitRequestResetPasswordForm"
          @keydown.enter.stop.prevent="submitRequestResetPasswordForm"
        >
          <!--
          Use `model-value` and `@input` here instead of `v-model`
          because the input value doesn't get updated on every keypress on android when using `v-model`
          @see https://github.com/vuejs/vue/issues/9777#issuecomment-478831263
        -->

          <OzLabelInput class="mb-4 tablet-portrait:mb-8">
            <template #label>
              <label
                for="email-input"
                :class="['text-body-small-posts', 'text-dark-text-100 dark:text-light-text-100', 'px-2']"
              >
                {{ __('Email') }}
              </label>
            </template>

            <template #input
              ><OzInput
                id="email-input"
                ref="emailInput"
                name="email"
                type="email"
                test-id="emailInput"
                :model-value="email"
                :validation-message="emailValidationMessage"
                placeholder="harrypotter@hogwarts.edu"
                :valid="isEmailValid ? 'yes' : 'no'"
                class="bg-light-ui-100 dark:bg-dark-ui-100"
                @update:modelValue="onInputEmailInput"
                @focusin="onFocusInEmailInput"
                @blur="onBlurEmailInput"
            /></template>
          </OzLabelInput>

          <div class="flex justify-center tablet-portrait:justify-end">
            <OzContainedButton
              class="w-full tablet-portrait:w-auto min-w-0"
              :disabled="!canSubmitRequestResetPasswordForm"
              :text="__('Request password reset')"
              data-testid="requestPasswordResetButton"
            />
          </div>
        </form>
      </template>
      <template
        v-if="
          requestResetPasswordStep === RequestResetPasswordStep.KnownEmail ||
          requestResetPasswordStep === RequestResetPasswordStep.KnownEmailResent
        "
      >
        <div class="text-dark-text-100 dark:text-light-text-100 mb-5">
          <OzBackButton v-if="device.desktop" class="mb-5" @click="handleBackButton" />
          <h1 class="w-56 text-login-heading-1 font-semibold px-2 mb-1">
            {{ __('Check email') }}
          </h1>
          <h2 class="text-login-body px-2 break-word-anywhere" v-html="knownEmailDescription" />
        </div>

        <div class="flex justify-center tablet-portrait:justify-end mb-6 tablet-portrait:mb-18">
          <OzContainedButton
            class="w-full tablet-portrait:w-auto min-w-0"
            data-testid="requestPasswordResendEmailButton"
            :disabled="secondsToResend !== 0"
            @click="resendRequestResetPasswordEmail"
          >
            <template v-if="secondsToResend === 0">
              {{ __('Resend email') }}
            </template>
            <div v-else class="w-full flex justify-between gap-4">
              <span class="truncate">{{ __('Resend email') }}</span>
              <span>{{ formattedSecondsToResend }}</span>
            </div>
          </OzContainedButton>
        </div>

        <p
          class="mb-6 text-center tablet-portrait:text-start text-body-small-posts tablet-portrait:text-body-extra-small text-dark-text-200 dark:text-light-text-200"
          v-html="knownEmailContactUsText"
        />
      </template>
      <template v-if="requestResetPasswordStep === RequestResetPasswordStep.UnknownEmail">
        <div class="text-dark-text-100 dark:text-light-text-100 mb-5">
          <OzBackButton v-if="device.desktop" class="mb-5" @click="handleBackButton" />
          <h1 class="w-56 text-login-heading-1 font-semibold px-2 mb-1">
            {{ __('Account not found') }}
          </h1>
          <h2 class="text-login-body px-2 break-word-anywhere" v-html="unknownEmailDescription" />
        </div>

        <div
          class="flex flex-col tablet-portrait:flex-row justify-center tablet-portrait:justify-end mb-6 tablet-portrait:mb-18 gap-5 tablet-portrait:gap-2"
        >
          <OzContainedButton
            class="w-full tablet-portrait:w-auto min-w-0"
            :color-scheme="OzContainedButtonColorScheme.Secondary"
            :text="__('Try another email')"
            data-testid="requestResetPasswordTryAnotherEmailButton"
            @click="tryAnotherEmail"
          />
          <OzContainedButton
            class="min-w-0"
            data-testid="requestResetPasswordSignUpButton"
            :href="signUpWithEmailUrl"
            :text="__('Sign up')"
          />
        </div>

        <p
          class="mb-6 text-center tablet-portrait:text-start text-body-small-posts tablet-portrait:text-body-extra-small text-dark-text-200 dark:text-light-text-200"
          v-html="unknownEmailContactUsText"
        />
      </template>
    </main>

    <AuthBottomNav
      v-if="device.desktop"
      class="fixed start-6 bottom-6 hidden tablet-portrait:flex"
      @change-locale="reloadPage"
    />
    <GlobalSnackbar />
  </div>
</template>

<style lang="postcss">
@import '@@/styles/4/auth';
</style>
